<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'contractEvents',
    path: '/contract-events',
    component: 'ContractEvents',
    title: () => pgettext('ContractEvents|Title', 'Contract Events'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractEvents',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const gridName = ref('contractEvents');
  const dsParams = ref({
    date_from: $fn.value.today(),
    date_to: null,
  });
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
    selectable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'event_name',
      type: 'command',
      name: $fn.value.pgettext('ContractEvents', 'Name'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('ContractEvents', 'Description'),
    },
    {
      field: 'contract',
      name: $fn.value.pgettext('ContractEvents', 'Contract'),
      type: 'command',
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    {{ $value.contract_number }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'contract.id',
            },
          },
        ],
      },
    },
    {
      field: 'timestamp',
      name: $fn.value.pgettext('ContractEvents', 'Timestamp'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'identity',
      name: $fn.value.pgettext('ContractEvents', 'User'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    dsParams: dsParams.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_contract_events', {
      parameters: () => dsParams.value,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'ContractEvents.dsParams' },
      },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      dsParams,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ContractEvents|Title', 'Contract Events'),
      icon: $config.value.contract.views.contractEvents.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_q9byqny1l_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_f0r237rg7_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_1ei8oo7qi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'DataViewerModal',
      props: {
        content: $event.data.data.event_data,
        mode: 'json',
      },
    };
  };
  const inst_evh_2248226175642056_command_5vanc7kq5_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'event_name';
  };
  const act_2248226175642056_showModal_cb2dmnwqg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_ozvaa0226_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'number';
  };
  const act_2248226175642056_showModal_hfhfoqpts_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.parent_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_asg1xp457_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'parent_number';
  };
  const act_4615685607825208_setData_ec462vkel_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'dsParams.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_setData_mwalzmxuk_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'dsParams.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_reloadSlickgrid_cbjzivsi9_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };

  const act_7315092382398562_closeModal_q9byqny1l =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_q9byqny1l_getActionArgs,
    });
  const inst_evh_7315092382398562_close_bgo7lmxu6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_q9byqny1l,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_bgo7lmxu6],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_1di6kjzwh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_q1tli6xle =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1di6kjzwh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_q1tli6xle],
    event: 'reload',
  });
  const act_2248226175642056_setData_f0r237rg7 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_f0r237rg7_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_5yx2o8x0q =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_f0r237rg7,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_5yx2o8x0q],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_1ei8oo7qi =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_1ei8oo7qi_getActionArgs,
    });
  const inst_evh_2248226175642056_command_5vanc7kq5 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_1ei8oo7qi,
      event: 'command',
      when: inst_evh_2248226175642056_command_5vanc7kq5_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_cb2dmnwqg =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_cb2dmnwqg_getActionArgs,
    });
  const inst_evh_2248226175642056_command_ozvaa0226 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_cb2dmnwqg,
      event: 'command',
      when: inst_evh_2248226175642056_command_ozvaa0226_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_hfhfoqpts =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_hfhfoqpts_getActionArgs,
    });
  const inst_evh_2248226175642056_command_asg1xp457 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_hfhfoqpts,
      event: 'command',
      when: inst_evh_2248226175642056_command_asg1xp457_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_5vanc7kq5,
      inst_evh_2248226175642056_command_ozvaa0226,
      inst_evh_2248226175642056_command_asg1xp457,
    ],
    event: 'command',
  });
  const act_4615685607825208_setData_ec462vkel = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_ec462vkel_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_bzl3c603g =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_ec462vkel,
      event: 'changed',
      displayName: 'setData',
    });
  const act_4615685607825208_setData_mwalzmxuk = new actionTypes.SetDataAction({
    actionArgs: act_4615685607825208_setData_mwalzmxuk_getActionArgs,
  });
  const inst_evh_4615685607825208_changed_qrjoyel30 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_setData_mwalzmxuk,
      event: 'changed',
      displayName: 'setData',
    });
  const act_4615685607825208_reloadSlickgrid_cbjzivsi9 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_4615685607825208_reloadSlickgrid_cbjzivsi9_getActionArgs,
    });
  const inst_evh_4615685607825208_changed_4z6xzbov7 =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadSlickgrid_cbjzivsi9,
      event: 'changed',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_4615685607825208_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_4615685607825208_changed_bzl3c603g,
      inst_evh_4615685607825208_changed_qrjoyel30,
      inst_evh_4615685607825208_changed_4z6xzbov7,
    ],
    event: 'changed',
  });

  const actions = {
    inst_evh_7315092382398562_close_bgo7lmxu6,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_q1tli6xle,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_5yx2o8x0q,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_5vanc7kq5,
    inst_evh_2248226175642056_command_ozvaa0226,
    inst_evh_2248226175642056_command_asg1xp457,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_changed_bzl3c603g,
    inst_evh_4615685607825208_changed_qrjoyel30,
    inst_evh_4615685607825208_changed_4z6xzbov7,
    inst_evh_4615685607825208_changed,
  };

  const evh_7315092382398562_close_bgo7lmxu6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_bgo7lmxu6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_q1tli6xle = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_q1tli6xle.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_5yx2o8x0q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_5yx2o8x0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_5vanc7kq5 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_5vanc7kq5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_ozvaa0226 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_ozvaa0226.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_asg1xp457 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_asg1xp457.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_bzl3c603g = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_bzl3c603g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_qrjoyel30 = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_qrjoyel30.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed_4z6xzbov7 = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed_4z6xzbov7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_changed = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractEvents"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      :name="gridName"
      :headerTitle="documentTitle"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <DateRangeSelector
            name="contractEventFilters"
            :from="dsParams.date_from"
            :to="dsParams.date_to"
            size="sm"
            @changed="evh_4615685607825208_changed($event, {})"
          >
          </DateRangeSelector>
          <PageHelp path="/general/jobs.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
